<template>
  <div class="employee-ack-summary-cards">
    <EmployeeAcknowledgementSummaryCardsSkeleton
      v-if="isLoading"
      class="summary-cards-skeleton"
    />
    <template v-else>
      <EmployeeAcknowledgementUpToDateCard
        :percentage="upToDatePct"
        :compact="compactCards"
        :selected="isCardSelected(['acknowledgement_ok'])"
        @click="handleCardClick(['acknowledgement_ok'])"
      />
      <EmployeeAcknowledgementSummaryCard
        v-for="card in summaryCards"
        :key="card.key"
        :icon="card.icon"
        :icon-class="card.iconClass"
        :border-class="card.borderClass"
        :label="card.label"
        :value="card.value"
        :tooltip="card.tooltip"
        :selected="isCardSelected(card.optionIds)"
        :compact="compactCards"
        @click="handleCardClick(card.optionIds)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { getTooltip } from '@/helpers/tooltips';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useAppBase } from '@/stores/app/base';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useRouter, useRoute } from 'vue-router';
import EmployeeAcknowledgementSummaryCardsSkeleton from './EmployeeAcknowledgementSummaryCardsSkeleton.vue';
import EmployeeAcknowledgementUpToDateCard from './EmployeeAcknowledgementUpToDateCard.vue';
import EmployeeAcknowledgementSummaryCard from './EmployeeAcknowledgementSummaryCard.vue';

const COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH = 1400;

const props = defineProps({
  totals: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const { selectAcknowledgementStatus, updateSelectedAcknowledgementStatuses } = useEmployeeAcknowledgementsStore();
const { selectedAcknowledgementStatuses } = storeToRefs(useEmployeeAcknowledgementsStore());
const { windowWidth } = storeToRefs(useAppBase());
const { open: filterDrawerOpen } = storeToRefs(useFilterDrawer());
const router = useRouter();
const route = useRoute();

const upToDatePct = computed(() => {
  const total = props.totals.total_employees || 0;

  if (!total) return 0;

  return Math.round(((props.totals.acknowledgement_ok || 0) / total) * 100);
});

const compactCards = computed(() => (
  filterDrawerOpen.value && windowWidth.value < COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH
));

const handleCardClick = (optionIds) => {
  selectAcknowledgementStatus(optionIds, router);
};

const isCardSelected = (optionIds) => {
  const selected = [...selectedAcknowledgementStatuses.value].sort();
  const sorted = [...optionIds].sort();

  return selected.length === sorted.length
    && selected.every((id, i) => id === sorted[i]);
};

const summaryCards = computed(() => {
  const { totals = {} } = props;

  return [
    {
      key: 'no_acknowledgement',
      label: 'Nothing on File',
      value: totals.no_acknowledgement || 0,
      borderClass: 'border-no-acknowledgement',
      iconClass: 'icon-no-acknowledgement',
      icon: 'cross-outline',
      optionIds: ['no_acknowledgement'],
      tooltip: getTooltip('acknowledgement-alert-no-ack'),
    },
    {
      key: 'pending_acknowledgement',
      label: 'Pending Signature',
      value: totals.pending_acknowledgement || 0,
      borderClass: 'border-incomplete',
      iconClass: 'icon-incomplete',
      icon: 'docs',
      optionIds: ['pending_acknowledgement'],
      tooltip: getTooltip('acknowledgement-alert-incomplete'),
    },
    {
      key: 'job_profile_changed',
      label: 'Job Profile Changed',
      value: (totals.job_profile_id_changed || 0) + (totals.job_profile_content_changed || 0),
      borderClass: 'border-job-profile-changed',
      iconClass: 'icon-job-profile-changed',
      icon: 'changed',
      optionIds: ['job_profile_id_changed', 'job_profile_content_changed'],
      tooltip: getTooltip('acknowledgement-alert-profile-changed'),
    },
    {
      key: 'acknowledgement_out_of_date',
      label: 'Out of Date',
      value: totals.acknowledgement_out_of_date || 0,
      borderClass: 'border-out-of-date',
      iconClass: 'icon-out-of-date',
      icon: 'briefcase',
      optionIds: ['acknowledgement_out_of_date'],
      tooltip: getTooltip('acknowledgement-alert-out-of-date'),
    },
  ];
});

onMounted(() => {
  if (route.query?.acknowledgement_alert) {
    if (Array.isArray(route.query.acknowledgement_alert)) {
      updateSelectedAcknowledgementStatuses(route.query.acknowledgement_alert);
    } else {
      updateSelectedAcknowledgementStatuses(route.query.acknowledgement_alert.split(','));
    }
  }
});
</script>

<style lang="less" scoped>
.employee-ack-summary-cards {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  min-width: 0;
}

.summary-cards-skeleton {
  grid-column: 1 / -1;
  min-width: 0;
}
</style>
